import React, { useState } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Tooltip,
  IconButton,
  Button,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getOnboardingDownloadData } from '../../store/Slice/Onboarding/OnboardingSlice';

export default function PartnersListToolbar({
  filterName,
  onFilterName,
  parent = 'partners',
  filterState,
  setFilterState,
  statusOptions = [],
  pageMenu
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenFilter, setOpenFilter] = useState(false);
  const { CityDrop, PartnerStatusDrop } = useGlobalSelector();
  const { LoginData } = useLoginSelector();
  const isSearchEnabled = pageMenu?.some(
    (pageElement) => pageElement?.subPageCode === 'SEARCH' && pageElement?.isEnable === true
  );
  const isFilterEnabled = pageMenu?.some(
    (pageElement) => pageElement?.subPageCode === 'FILTER' && pageElement?.isEnable === true
  );

  const handleDownload = () => {
    if (!LoginData) return;
    const data = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      accessLevelId: LoginData?.accessLevelId,
      entityListRequestModel: {
        searchLiteral: filterName || '',
        pageSize: 10,
        pageNumber: 1,
        sortOrder: 'desc'
      },
      filterModel: {
        typeId: filterState?.typeId ?? null,
        partnerStatusTypeId: filterState?.partnerStatusTypeId ?? null,
        cityId: filterState?.cityId ?? null
      }
    };
    dispatch(getOnboardingDownloadData(data));
  };

  const handleRemoveFilter = () => {
    if (setFilterState) {
      setFilterState((prev) => ({
        ...prev,
        typeId: null,
        partnerStatusTypeId: null,
        city: null,
        cityId: null,
        pageNumber: 1
      }));
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      {/* Top Controls Bar */}
      <Box
        sx={{
          minHeight: 64,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        {/* Left Export Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={handleDownload}
            sx={{
              borderColor: '#e2e8f0',
              color: '#475569',
              borderRadius: '10px',
              px: 2,
              py: 0.9,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#ffffff',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
            }}
          >
            Export Data
          </Button>
        </Box>

        {/* Right Search & Filter Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isSearchEnabled && (
            <TextField
              size="small"
              value={filterName}
              onChange={onFilterName}
              placeholder="Search Partners..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: '100%', sm: 260, md: 280 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  fontSize: '13px',
                  '& fieldset': {
                    borderColor: '#e2e8f0'
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0284c7'
                  }
                }
              }}
            />
          )}

          {isFilterEnabled && (
            <Button
              variant="outlined"
              onClick={() => setOpenFilter((prev) => !prev)}
              startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '13px',
                color: isOpenFilter ? '#0284c7' : '#475569',
                borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
                bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#ffffff',
                height: 40,
                px: 2,
                '&:hover': {
                  borderColor: '#0284c7',
                  bgcolor: 'rgba(2, 132, 199, 0.08)'
                }
              }}
            >
              Filters
            </Button>
          )}
        </Box>
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isFilterEnabled && isOpenFilter}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Partner Type */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState?.typeId ?? ' '}
                onChange={(e) => {
                  const val = e.target.value === ' ' ? null : e.target.value;
                  if (setFilterState) {
                    setFilterState((prev) => ({ ...prev, typeId: val, pageNumber: 1 }));
                  }
                }}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select Partner Type
                </MenuItem>
                {PartnerStatusDrop?.length > 0 &&
                  PartnerStatusDrop.map((item) => (
                    <MenuItem value={item?.value} key={item?.value} sx={{ fontSize: 13 }}>
                      {item?.label}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>

            {/* City Autocomplete */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Autocomplete
                id="combo-box-city"
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select City"
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                    }}
                  />
                )}
                value={filterState?.city ?? null}
                options={CityDrop || []}
                getOptionLabel={(option) => option.label || ''}
                renderOption={(props, option) => (
                  <li {...props} key={option.value || option.label} style={{ fontSize: 13 }}>
                    {option.label}
                  </li>
                )}
                onChange={(e, newValue) => {
                  if (setFilterState) {
                    setFilterState((prev) => ({
                      ...prev,
                      city: newValue ?? null,
                      cityId: newValue ? newValue.value : null,
                      pageNumber: 1
                    }));
                  }
                }}
              />
            </Grid>

            {/* Status Select */}
            <Grid item xs={12} sm={3} md={3.5}>
              <Select
                fullWidth
                size="small"
                onChange={(e) => {
                  const val = e.target.value === ' ' ? null : e.target.value;
                  if (setFilterState) {
                    setFilterState((prev) => ({
                      ...prev,
                      partnerStatusTypeId: val,
                      pageNumber: 1
                    }));
                  }
                }}
                value={filterState?.partnerStatusTypeId ?? ' '}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select Status
                </MenuItem>
                {statusOptions?.map((item) => (
                  <MenuItem key={item.value} value={item.value} sx={{ fontSize: 13 }}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={1} md="auto">
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
